import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Ban,
  CreditCard,
  FilePlus2,
  FileText,
  Mail,
  Plus,
  RefreshCw,
  Receipt,
  Save,
  Trash2,
  TrendingUp,
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import AppModal from "../../components/AppModal";
import PdfViewer from "../../components/PdfViewer";
import SqlImportButton from "../../components/SqlImportButton";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";

const documentTypes = [
  { value: "FA", label: "Factura" },
  { value: "FT", label: "Factura a credito" },
  { value: "FR", label: "Factura-recibo" },
  { value: "FP", label: "Factura pro-forma" },
  { value: "NE", label: "Nota de entrega" },
];

const paymentMethods = ["TPA", "Dinheiro", "Transferencia"];
const taxCodes = [
  { value: "NOR", label: "NOR - Normal", percentage: 14 },
  { value: "ISE", label: "ISE - Isento", percentage: 0 },
  { value: "OUT", label: "OUT - Outro", percentage: 0 },
];

const emptyItem = {
  servico_id: "",
  quarto_id: "",
  data_check_in: "",
  data_check_out: "",
  preco_unitario: "",
  qtd_dias: 1,
  desconto_valor: 0,
  tax_code: "NOR",
  iva: 14,
};

const emptyDoc = {
  tipo: "FA",
  serie_id: "",
  cliente_id: "",
  data_emissao: new Date().toISOString().slice(0, 10),
  data_vencimento: "",
  observacoes: "",
  metodo_pagamento: "TPA",
  valor_pago: "",
  itens: [{ ...emptyItem }],
};

const emptyAction = {
  motivo: "",
  serie_id: "",
  valor: "",
  iva: 14,
  tax_code: "NOR",
  valor_pago: "",
  metodo_pagamento: "Dinheiro",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const money = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) => {
  return money(value).toLocaleString("pt-AO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const daysBetween = (start, end) => {
  if (!start || !end) return 1;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  const days = Math.round((endDate - startDate) / 86400000);
  return days > 0 ? days : 1;
};

const statusClass = (estado) => {
  if (estado === "Validado") return "bg-success-subtle text-success";
  if (estado === "Anulado") return "bg-danger-subtle text-danger";
  if (estado === "N/Validado") return "bg-warning-subtle text-warning";
  return "bg-secondary-subtle text-secondary";
};

const Documento = ({ initialTab = "emitir" }) => {
  const { user } = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [clientes, setClientes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [quartos, setQuartos] = useState([]);
  const [series, setSeries] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [pdfFormat, setPdfFormat] = useState("A4");
  const [pdfView, setPdfView] = useState(null);
  const [doc, setDoc] = useState(emptyDoc);
  const [action, setAction] = useState({ type: null, document: null });
  const [actionForm, setActionForm] = useState(emptyAction);

  const fetchData = useCallback(async (signal) => {
    if (!user?.empresa_id) return;

    setLoadingData(true);
    setError(null);

    const [clientesResult, servicosResult, quartosResult, seriesResult, documentosResult, empresaResult] = await Promise.allSettled([
      api.get("/clientes", { signal }),
      api.get("/servicos", { signal }),
      api.get("/quartos", { signal }),
      api.get("/series", { signal }),
      api.get("/documentos", { signal }),
      api.get(`/empresas/${user.empresa_id}`, { signal }),
    ]);

    if (signal?.aborted) return;

    if (clientesResult.status === "fulfilled") {
      const items = clientesResult.value.data?.data || [];
      setClientes(items.filter((item) => Number(item.empresa_id) === Number(user.empresa_id) && !item.deletedAt));
    }

    if (servicosResult.status === "fulfilled") {
      const items = servicosResult.value.data?.data || [];
      setServicos(items.filter((item) => Number(item.empresa_id) === Number(user.empresa_id) && !item.deletedAt));
    }

    if (quartosResult.status === "fulfilled") {
      const items = quartosResult.value.data?.data || [];
      setQuartos(items.filter((item) => Number(item.empresa_id) === Number(user.empresa_id) && !item.deletedAt));
    }

    if (seriesResult.status === "fulfilled") {
      const items = seriesResult.value.data?.data || [];
      setSeries(items.filter((item) => Number(item.empresa_id) === Number(user.empresa_id) && !item.deletedAt && Number(item.status) === 1));
    }

    if (documentosResult.status === "fulfilled") {
      setDocumentos(documentosResult.value.data?.data || []);
    }
    if (empresaResult.status === "fulfilled") setEmpresa(empresaResult.value.data?.data || null);

    const failed = [clientesResult, servicosResult, quartosResult, seriesResult, documentosResult, empresaResult].find((result) => result.status === "rejected");
    if (failed) {
      setError(failed.reason?.message || "Nao foi possivel carregar todos os dados.");
    }

    setLoadingData(false);
  }, [user?.empresa_id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const allowsIva = String(empresa?.regime_tributario || "").toLowerCase().includes("geral");
  const availableTaxCodes = allowsIva ? taxCodes : taxCodes.filter((tax) => tax.value === "ISE");

  useEffect(() => {
    if (!empresa || allowsIva) return;
    setDoc((current) => ({ ...current, itens: current.itens.map((item) => ({ ...item, tax_code: "ISE", iva: 0 })) }));
    setActionForm((current) => ({ ...current, tax_code: "ISE", iva: 0 }));
  }, [empresa, allowsIva]);

  const totals = useMemo(() => {
    return doc.itens.reduce((acc, item) => {
      const lineBase = Math.max(0, (money(item.preco_unitario) * money(item.qtd_dias || 1)) - money(item.desconto_valor));
      const tax = lineBase * (money(item.iva) / 100);
      return {
        subtotal: acc.subtotal + lineBase,
        iva: acc.iva + tax,
        total: acc.total + lineBase + tax,
      };
    }, { subtotal: 0, iva: 0, total: 0 });
  }, [doc.itens]);

  const filteredDocuments = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return documentos;

    return documentos.filter((item) => {
      return [
        item.numero,
        item.tipo,
        item.estado,
        item.cliente?.nome,
        item.numero_fatura_origem,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
    });
  }, [documentos, filter]);

  const seriesForType = useMemo(() => {
    return series.filter((serie) => serie.tipo === doc.tipo);
  }, [doc.tipo, series]);

  const selectedSerie = useMemo(() => {
    return series.find((serie) => String(serie.id) === String(doc.serie_id));
  }, [doc.serie_id, series]);

  const documentNumberPreview = useMemo(() => {
    if (!selectedSerie) return "";
    const prefix = `${doc.tipo} ${selectedSerie.nome}/`;
    const count = documentos.filter((item) => item.tipo === doc.tipo && String(item.numero || "").startsWith(prefix)).length;
    const first = Math.max(1, Number(selectedSerie.primeiroDocumento || 1));
    return `${prefix}${first + count}`;
  }, [doc.tipo, documentos, selectedSerie]);

  const isRoomBlocked = useCallback((room, item) => {
    if (!room || room.status !== "disponivel") return true;
    if (!item.data_check_in || !item.data_check_out) return false;

    return documentos.some((documento) => {
      if (documento.estado === "Anulado") return false;
      return (documento.itens || []).some((line) => {
        return String(line.quarto_id) === String(room.id)
          && line.data_check_in
          && line.data_check_out
          && item.data_check_in < line.data_check_out
          && item.data_check_out > line.data_check_in;
      });
    });
  }, [documentos]);

  const updateDoc = (field, value) => {
    setDoc((current) => ({
      ...current,
      [field]: value,
      ...(field === "tipo" ? { serie_id: "" } : {}),
    }));
  };

  const updateItem = (index, field, value) => {
    setDoc((current) => {
      const itens = current.itens.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const next = { ...item, [field]: value };
        if (field === "tax_code") {
          const tax = taxCodes.find((entry) => entry.value === value);
          next.iva = tax?.percentage ?? 0;
        }

        if (field === "servico_id") {
          const service = servicos.find((servico) => String(servico.id) === String(value));
          const selectedRoom = quartos.find((quarto) => String(quarto.id) === String(next.quarto_id));
          if (selectedRoom && String(selectedRoom.servico_id || "") !== String(value || "")) next.quarto_id = "";
          if (service?.preco) {
            next.preco_unitario = service.preco;
          }
        }

        if (field === "quarto_id") {
          const room = quartos.find((quarto) => String(quarto.id) === String(value));
          if (room?.servico_id) {
            next.servico_id = room.servico_id;
            const service = servicos.find((servico) => String(servico.id) === String(room.servico_id));
            if (service?.preco) {
              next.preco_unitario = service.preco;
            }
          }
        }

        if (field === "data_check_in" || field === "data_check_out") {
          next.qtd_dias = daysBetween(next.data_check_in, next.data_check_out);
        }

        return next;
      });

      return { ...current, itens };
    });
  };

  const addItem = () => {
    setDoc((current) => ({ ...current, itens: [...current.itens, { ...emptyItem, ...(!allowsIva ? { tax_code: "ISE", iva: 0 } : {}) }] }));
  };

  const removeItem = (index) => {
    setDoc((current) => {
      const itens = current.itens.filter((_, itemIndex) => itemIndex !== index);
      return { ...current, itens: itens.length ? itens : [{ ...emptyItem, ...(!allowsIva ? { tax_code: "ISE", iva: 0 } : {}) }] };
    });
  };

  const submitDocument = async (event) => {
    event.preventDefault();
    const isProforma = String(doc.tipo || "").trim().toUpperCase() === "FP";

    if (!doc.cliente_id) {
      await notify("Selecione o cliente.", "warning");
      return;
    }

    if (!doc.serie_id) {
      await notify("Selecione a serie do documento.", "warning");
      return;
    }

    if (!doc.itens.some((item) => money(item.preco_unitario) > 0)) {
      await notify("Adicione pelo menos uma linha com valor.", "warning");
      return;
    }

    const invalidRoomLine = !isProforma && doc.itens.find((item) => item.quarto_id && (!item.data_check_in || !item.data_check_out));
    if (invalidRoomLine) {
      await notify("Informe check-in e check-out nas linhas com quarto.", "warning");
      return;
    }

    const invalidPeriodLine = !isProforma && doc.itens.find((item) => item.data_check_in && item.data_check_out && item.data_check_out <= item.data_check_in);
    if (invalidPeriodLine) {
      await notify("O check-out deve ser posterior ao check-in.", "warning");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...doc,
        valor_pago: doc.valor_pago,
        itens: doc.itens.map((item) => ({
          ...item,
          data_check_in: isProforma ? null : item.data_check_in,
          data_check_out: isProforma ? null : item.data_check_out,
          qtd_dias: !isProforma && item.data_check_in && item.data_check_out ? daysBetween(item.data_check_in, item.data_check_out) : Number(item.qtd_dias || 1),
          preco_unitario: money(item.preco_unitario),
          desconto_valor: money(item.desconto_valor),
          tax_code: item.tax_code,
          iva: money(item.iva),
        })),
    };

    if (["FT", "FR", "RC"].includes(doc.tipo) && money(doc.valor_pago) <= 0) {
      await notify("Informe um valor pago maior que zero.", "warning");
      return;
    }

      const response = await api.post("/documentos", payload);
      const createdDocument = response.data?.data;
      await notify(doc.tipo === "FP" || doc.tipo === "NE"
        ? "Documento emitido localmente."
        : "Documento emitido e enviado para a AGT.", "success");

      setDoc({ ...emptyDoc, itens: [{ ...emptyItem, ...(!allowsIva ? { tax_code: "ISE", iva: 0 } : {}) }] });
      await fetchData();
      setActiveTab("emitidos");
      if (createdDocument?.id) {
        setPdfView({
          title: createdDocument.numero,
          path: `/pdf/documentos/${createdDocument.id}?formato=${pdfFormat}&duas_vias=1`,
          filename: `${createdDocument.numero}-${pdfFormat}.pdf`,
        });
      }
    } catch (err) {
      await notify(err.message || "Nao foi possivel emitir o documento.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAction = (type, document) => {
    setAction({ type, document });
    const firstSerie = series.find((serie) => serie.tipo === type);
    setActionForm({
      ...emptyAction,
      ...(!allowsIva ? { tax_code: "ISE", iva: 0 } : {}),
      serie_id: firstSerie?.id || "",
      motivo: type === "NC" ? "Anulacao do documento original" : "Acerto de valor",
      valor: type === "ND" ? "" : document.total_bruto,
      valor_pago: ["RC", "FT", "FR"].includes(type) ? document.total_geral : "",
      metodo_pagamento: document.metodo_pagamento || "Dinheiro",
    });
    showModal("documentActionModal");
  };

  const submitAction = async (event) => {
    event.preventDefault();
    if (!action.document) return;

    if (!actionForm.serie_id) {
      await notify("Selecione a serie da operacao.", "warning");
      return;
    }

    const endpoint = {
      NC: `/documentos/${action.document.id}/nota-credito`,
      ND: `/documentos/${action.document.id}/nota-debito`,
      RC: `/documentos/${action.document.id}/recibo`,
      FT: `/documentos/${action.document.id}/converter-proforma`,
      FR: `/documentos/${action.document.id}/converter-proforma`,
    }[action.type];

    try {
      setActionLoading(true);
      await api.post(endpoint, { ...actionForm, tipo: action.type });
      await notify(
        ["FT", "FR"].includes(action.type) ? `Pro-forma convertida em ${action.type} e comunicada a AGT.` : action.type === "NC" ? "Nota de credito emitida." : action.type === "ND" ? "Nota de debito emitida." : "Recibo emitido.",
        "success"
      );
      closeModal("documentActionModal");
      await fetchData();
    } catch (err) {
      await notify(err.message || "Nao foi possivel concluir a operacao.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const resendToAgt = async (document) => {
    try {
      setActionLoading(true);
      await api.post(`/documentos/${document.id}/reenviar-agt`);
      await notify("Documento reenviado e comunicado a AGT.", "success");
      await fetchData();
    } catch (err) {
      await notify(err.message || "Nao foi possivel reenviar o documento a AGT.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const sendDocumentEmail = async (document) => {
    try {
      setActionLoading(true);
      await api.post(`/documentos/${document.id}/enviar-email`, { formato: pdfFormat });
      await notify(`Documento enviado para ${document.cliente.email}.`, "success");
    } catch (requestError) {
      await notify(requestError.response?.data?.message || "Nao foi possivel enviar o documento por email.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const canManage = ["admin", "operador", "caixa"].includes(user?.nivel);
  const canImport = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Documentos"
      breadcrumb={["Comercial", "Documentos"]}
      actions={canImport ? (
        <>
          <SqlImportButton endpoint="/importar/sql" label="Importar tudo" disabled={loadingData} onImported={() => fetchData()} />
          <SqlImportButton endpoint="/importar/documentos" label="Importar facturas" disabled={loadingData} onImported={() => fetchData()} />
        </>
      ) : null}
    >
      {error && <div className="alert alert-warning py-2">{error}</div>}
      {!canManage && <div className="alert alert-warning py-2">Nao tem permissoes para emitir documentos.</div>}

      <div className="document-tabs">
        <button className={activeTab === "emitir" ? "active" : ""} type="button" onClick={() => setActiveTab("emitir")}>
          <FilePlus2 size={16} />
          Emitir
        </button>
        <button className={activeTab === "emitidos" ? "active" : ""} type="button" onClick={() => setActiveTab("emitidos")}>
          <FileText size={16} />
          Documentos emitidos
        </button>
      </div>

      {activeTab === "emitir" ? (
        <form className="card app-card bg-white" onSubmit={submitDocument}>
          <div className="card-body">
            <div className="row">
              <div className="col-md-2 mb-3">
                <label className="form-label" htmlFor="tipo">Tipo</label>
                <select className="form-select" id="tipo" value={doc.tipo} onChange={(event) => updateDoc("tipo", event.target.value)}>
                  {documentTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="serie_id">Serie</label>
                <select className="form-select" id="serie_id" value={doc.serie_id} onChange={(event) => updateDoc("serie_id", event.target.value)} required>
                  <option value="">Selecione</option>
                  {seriesForType.map((serie) => <option key={serie.id} value={serie.id}>{serie.nome}</option>)}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="cliente_id">Cliente</label>
                <select className="form-select" id="cliente_id" value={doc.cliente_id} onChange={(event) => updateDoc("cliente_id", event.target.value)} required>
                  <option value="">Selecione</option>
                  {clientes.map((cliente) => <option key={cliente.id} value={cliente.id}>{cliente.nome}</option>)}
                </select>
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label" htmlFor="data_emissao">Emissao</label>
                <input className="form-control" id="data_emissao" type="date" value={doc.data_emissao} onChange={(event) => updateDoc("data_emissao", event.target.value)} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label" htmlFor="data_vencimento">Vencimento</label>
                <input className="form-control" id="data_vencimento" type="date" value={doc.data_vencimento} onChange={(event) => updateDoc("data_vencimento", event.target.value)} />
              </div>
              {["FT", "FR", "RC"].includes(doc.tipo) && <div className="col-md-2 mb-3">
                <label className="form-label" htmlFor="document_valor_pago">Valor pago</label>
                <input className="form-control" id="document_valor_pago" type="number" min="0.01" step="0.01" value={doc.valor_pago} onChange={(event) => updateDoc("valor_pago", event.target.value)} required />
              </div>}
            </div>

            <div className="document-number-preview">
              <span>Numero previsto</span>
              <strong>{documentNumberPreview || "Selecione uma serie activa"}</strong>
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="pdf_format">Formato PDF</label>
                <select className="form-select" id="pdf_format" value={pdfFormat} onChange={(event) => setPdfFormat(event.target.value)}>
                  <option value="A4">A4</option>
                  <option value="A5">A5</option>
                  <option value="TICKET">Ticket</option>
                </select>
              </div>
            </div>

            <div className="document-lines">
              <div className="document-lines-head">
                <strong>Itens</strong>
                <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={addItem}>
                  <Plus size={15} />
                  Linha
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Servico</th>
                      <th>Quarto</th>
                      <th>Check-in</th>
                      <th>Check-out</th>
                      <th>Preco</th>
                      <th>Dias</th>
                      <th>Desc.</th>
                      <th>IVA</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.itens.map((item, index) => {
                      const subtotal = Math.max(0, (money(item.preco_unitario) * money(item.qtd_dias || 1)) - money(item.desconto_valor));
                      return (
                        <tr key={`item-${index}`}>
                          <td>
                            <select className="form-select form-select-sm" value={item.servico_id} onChange={(event) => updateItem(index, "servico_id", event.target.value)}>
                              <option value="">Servico manual</option>
                              {servicos.map((servico) => <option key={servico.id} value={servico.id}>{servico.nome}</option>)}
                            </select>
                          </td>
                          <td>
                            <select className="form-select form-select-sm" value={item.quarto_id} onChange={(event) => updateItem(index, "quarto_id", event.target.value)}>
                              <option value="">Sem quarto</option>
                              {quartos.filter((quarto) => !item.servico_id || String(quarto.servico_id) === String(item.servico_id)).map((quarto) => {
                                const blocked = isRoomBlocked(quarto, item);
                                return (
                                  <option key={quarto.id} value={quarto.id} disabled={blocked}>
                                    {quarto.nome}{blocked ? " - indisponivel" : ""}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td>{doc.tipo === "FP" ? <span className="text-muted">—</span> : <input className="form-control form-control-sm" type="date" value={item.data_check_in} onChange={(event) => updateItem(index, "data_check_in", event.target.value)} />}</td>
                          <td>{doc.tipo === "FP" ? <span className="text-muted">—</span> : <input className="form-control form-control-sm" type="date" value={item.data_check_out} onChange={(event) => updateItem(index, "data_check_out", event.target.value)} />}</td>
                          <td><input className="form-control form-control-sm" type="number" min="0" step="0.01" value={item.preco_unitario} onChange={(event) => updateItem(index, "preco_unitario", event.target.value)} /></td>
                          <td><input className="form-control form-control-sm document-days-input" type="number" min="1" step="1" value={item.qtd_dias} onChange={(event) => updateItem(index, "qtd_dias", event.target.value)} readOnly={Boolean(item.data_check_in && item.data_check_out)} /></td>
                          <td><input className="form-control form-control-sm" type="number" min="0" step="0.01" value={item.desconto_valor} onChange={(event) => updateItem(index, "desconto_valor", event.target.value)} /></td>
                          <td>
                            <select className="form-select form-select-sm" value={item.tax_code} onChange={(event) => updateItem(index, "tax_code", event.target.value)}>
                              {availableTaxCodes.map((tax) => <option key={tax.value} value={tax.value}>{tax.value}</option>)}
                            </select>
                          </td>
                          <td className="text-end">{formatMoney(subtotal)}</td>
                          <td className="text-end">
                            <button className="btn btn-light btn-icon text-danger" type="button" title="Remover linha" onClick={() => removeItem(index)}>
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-md-7 mb-3">
                <label className="form-label" htmlFor="observacoes">Observacoes</label>
                <textarea className="form-control" id="observacoes" rows="3" value={doc.observacoes} onChange={(event) => updateDoc("observacoes", event.target.value)} />
              </div>
              <div className="col-md-5">
                <div className="document-totals">
                  <span>Subtotal <strong>{formatMoney(totals.subtotal)} AOA</strong></span>
                  <span>IVA <strong>{formatMoney(totals.iva)} AOA</strong></span>
                  <span className="total">Total <strong>{formatMoney(totals.total)} AOA</strong></span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-footer bg-white d-flex justify-content-end gap-2">
            <button className="btn btn-primary d-inline-flex align-items-center gap-1" type="submit" disabled={saving || loadingData || !canManage}>
              <Save size={16} />
              {saving ? "A emitir..." : "Emitir documento"}
            </button>
          </div>
        </form>
      ) : (
        <div className="card app-card bg-white">
          <div className="card-body">
            <div className="issued-toolbar">
              <div>
                <strong>{filteredDocuments.length}</strong>
                <span> documento(s)</span>
              </div>
              <input className="form-control form-control-sm" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Pesquisar documento, cliente ou estado" />
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Numero</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Emissao</th>
                    <th>Origem</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th className="text-end">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingData ? (
                    <tr><td colSpan="8" className="text-center text-muted py-4">A carregar documentos...</td></tr>
                  ) : filteredDocuments.length ? filteredDocuments.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.numero}</td>
                      <td>{item.tipo}</td>
                      <td>{item.cliente?.nome || "-"}</td>
                      <td>{item.data_emissao}</td>
                      <td>{item.numero_fatura_origem || "-"}</td>
                      <td className="text-end">{formatMoney(item.total_geral)} AOA</td>
                      <td><span className={`badge ${statusClass(item.estado)}`}>{item.estado}</span></td>
                      <td>
                        <div className="document-actions">
                          {item.cliente?.email && <button className="btn btn-light btn-icon" type="button" title={`Enviar por email para ${item.cliente.email}`} onClick={() => sendDocumentEmail(item)} disabled={actionLoading}><Mail size={15} /></button>}
                          {['N/Validado', 'Pendente'].includes(item.estado) && !['FP', 'NE'].includes(item.tipo) && <button className="btn btn-light btn-icon" type="button" title="Reenviar a AGT" onClick={() => resendToAgt(item)} disabled={actionLoading}><RefreshCw size={15} /></button>}
                          <button className="btn btn-light btn-icon" type="button" title="Anular por nota de credito" onClick={() => openAction("NC", item)} disabled={item.estado === "Anulado" || item.tipo === "NC" || ["FP", "NE"].includes(item.tipo)}>
                            <Ban size={15} />
                          </button>
                          <button className="btn btn-light btn-icon" type="button" title="Emitir nota de debito" onClick={() => openAction("ND", item)} disabled={item.estado === "Anulado" || ["FP", "NE"].includes(item.tipo)}>
                            <TrendingUp size={15} />
                          </button>
                          <button className="btn btn-light btn-icon" type="button" title="Emitir recibo" onClick={() => openAction("RC", item)} disabled={item.estado === "Anulado" || ["FP", "NE", "RC"].includes(item.tipo)}>
                            <Receipt size={15} />
                          </button>
                          {item.tipo === "FP" && item.estado !== "Anulado" && <>
                            <button className="btn btn-light btn-icon" type="button" title="Converter em FT" onClick={() => openAction("FT", item)}><TrendingUp size={15} /></button>
                            <button className="btn btn-light btn-icon" type="button" title="Converter em FR" onClick={() => openAction("FR", item)}><CreditCard size={15} /></button>
                          </>}
                          <button className="btn btn-light btn-icon" type="button" title="Previsualizar PDF" onClick={() => setPdfView({ title: item.numero, path: `/pdf/documentos/${item.id}?formato=${pdfFormat}&duas_vias=1`, filename: `${item.numero}-${pdfFormat}.pdf` })}>
                            <FileText size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" className="text-center text-muted py-4">Nenhum documento emitido.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <AppModal
        id="documentActionModal"
        title={action.type === "NC" ? "Anular por nota de credito" : action.type === "ND" ? "Emitir nota de debito" : ["FT", "FR"].includes(action.type) ? `Converter pro-forma em ${action.type}` : "Emitir recibo"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-1" form="documentActionForm" disabled={actionLoading}>
              {action.type === "RC" ? <CreditCard size={16} /> : <Save size={16} />}
              {actionLoading ? "A processar..." : "Confirmar"}
            </button>
          </>
        )}
      >
        <form id="documentActionForm" onSubmit={submitAction}>
          <div className="alert alert-light border py-2">
            <strong>{action.document?.numero}</strong> - {action.document?.cliente?.nome || "Cliente"} - {formatMoney(action.document?.total_geral)} AOA
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="action_serie_id">Serie</label>
            <select className="form-select" id="action_serie_id" value={actionForm.serie_id} onChange={(event) => setActionForm((current) => ({ ...current, serie_id: event.target.value }))} required>
              <option value="">Selecione</option>
              {series.filter((serie) => serie.tipo === action.type).map((serie) => <option key={serie.id} value={serie.id}>{serie.nome}</option>)}
            </select>
          </div>
          {!["RC", "FT", "FR"].includes(action.type) ? (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="valor">Valor base</label>
                <input className="form-control" id="valor" type="number" min="0" step="0.01" value={actionForm.valor} onChange={(event) => setActionForm((current) => ({ ...current, valor: event.target.value }))} required />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="action_tax_code">IVA</label>
                <select className="form-select" id="action_tax_code" value={actionForm.tax_code} onChange={(event) => {
                  const tax = taxCodes.find((entry) => entry.value === event.target.value);
                  setActionForm((current) => ({ ...current, tax_code: event.target.value, iva: tax?.percentage ?? 0 }));
                }}>
                  {availableTaxCodes.map((tax) => <option key={tax.value} value={tax.value}>{tax.label}</option>)}
                </select>
              </div>
              <div className="col-12 mb-3">
                <label className="form-label" htmlFor="motivo">Motivo</label>
                <textarea className="form-control" id="motivo" rows="3" value={actionForm.motivo} onChange={(event) => setActionForm((current) => ({ ...current, motivo: event.target.value }))} required />
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="valor_pago">Valor recebido</label>
                <input className="form-control" id="valor_pago" type="number" min="0" step="0.01" value={actionForm.valor_pago} onChange={(event) => setActionForm((current) => ({ ...current, valor_pago: event.target.value }))} required />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="metodo_pagamento">Metodo</label>
                <select className="form-select" id="metodo_pagamento" value={actionForm.metodo_pagamento} onChange={(event) => setActionForm((current) => ({ ...current, metodo_pagamento: event.target.value }))}>
                  {paymentMethods.map((method) => <option key={method} value={method}>{method}</option>)}
                </select>
              </div>
            </div>
          )}
        </form>
      </AppModal>

      <div className="pdf-format-floating">
        <span>Formato</span>
        <select className="form-select form-select-sm" value={pdfFormat} onChange={(event) => setPdfFormat(event.target.value)}>
          <option value="A4">A4</option>
          <option value="A5">A5</option>
          <option value="TICKET">Ticket</option>
        </select>
      </div>

      <PdfViewer
        title={pdfView?.title}
        path={pdfView?.path}
        filename={pdfView?.filename}
        onClose={() => setPdfView(null)}
      />
    </ResourcePage>
  );
};

export default Documento;
